<script lang="ts">
export default {
  inheritAttrs: false,
};
</script>

<script setup lang="ts">
import _ from "lodash";
import { twMerge } from "tailwind-merge";
import {
  computed,
  type SelectHTMLAttributes,
  useAttrs,
  inject,
  ref,
  watch
} from "vue";
import { type ProvideFormInline } from "./FormInline.vue";

interface FormSelectProps extends /* @vue-ignore */ SelectHTMLAttributes {
  value?: SelectHTMLAttributes["value"];
  modelValue?: SelectHTMLAttributes["value"];
  formSelectSize?: "sm" | "lg";
  name?: string;  // Made optional for non-VeeValidate use
  relativeError?: boolean
  hideError?: boolean  // Allow hiding the default error display
  showErrorBorder?: boolean  // Control when to show error border (default: true)
  formSubmitted?: boolean  // Flag to indicate form has been submitted
}

const selectRef = ref<HTMLSelectElement>();
const props = defineProps<FormSelectProps>();
const emit = defineEmits(['update:modelValue']);
const attrs = useAttrs();
const formInline = inject<ProvideFormInline>("formInline", false);

import { useStringManipulation } from "@/helpers/string-manipulation";
import {useField} from "vee-validate";
const {capitaliseFirstLetter} = useStringManipulation();

const computedClass = computed(() => {
      const isEmpty = localValue.value === "" || localValue.value === null || localValue.value === undefined;
      return twMerge([
        "disabled:bg-slate-100 disabled:cursor-not-allowed disabled:dark:bg-darkmode-800/50",
        "[&[readonly]]:bg-slate-100 [&[readonly]]:cursor-not-allowed [&[readonly]]:dark:bg-darkmode-800/50",
        "transition duration-200 ease-in-out w-full text-sm border-slate-200 shadow-sm rounded-md py-2 px-3 pr-8 focus:outline-none focus:border-black focus:ring-0 dark:bg-darkmode-800 dark:border-transparent",
        props.formSelectSize == "sm" && "text-xs py-1.5 pl-2 pr-8",
        props.formSelectSize == "lg" && "text-lg py-1.5 pl-4 pr-8",
        formInline && "flex-1",
        typeof attrs.class === "string" && attrs.class,
        isEmpty && "!text-slate-400/90" //
      ])
    }
);

// If `name` prop is provided, use VeeValidate's `useField`
let value = ref(props.modelValue || props.value || '');
let errorMessage = ref<string | null>(null);
let meta: any = { touched: false, validated: false };
let handleChange: ((value: any) => void) | null = null;

if (props.name) {
  const field = useField(() => props.name!, undefined, {
    validateOnValueUpdate: false,
    validateOnMount: false,
    initialValue: '' // Ensure initial value is empty string to match placeholder
  });

  value = field.value;
  errorMessage = field.errorMessage;
  meta = field.meta;
  handleChange = field.handleChange;

  // Watch for changes in the VeeValidate field value and emit them
  watch(value, (newValue) => {
    emit('update:modelValue', newValue);
  });
  
  // Ensure value starts as empty string if undefined/null to prevent grey flash
  if (value.value === undefined || value.value === null) {
    handleChange('');
  }
}

const localValue = computed({
  get() {
    if (props.name) {
      // Using vee-validate
      return value.value;
    }
    
    // Not using vee-validate - use props directly
    if (props.modelValue === undefined && props.value === undefined) {
      const firstOption = selectRef.value?.querySelectorAll("option")[0];
      return (
          firstOption !== undefined &&
          (firstOption.getAttribute("value") !== null
              ? firstOption.getAttribute("value")
              : firstOption.text)
      );
    }

    return props.modelValue === undefined ? props.value : props.modelValue;
  },
  set(newValue) {
    if (props.name && handleChange) {
      // Using vee-validate
      handleChange(newValue);
    } else {
      // Not using vee-validate - emit directly
      emit('update:modelValue', newValue);
    }
  },
});

// Watch for changes in localValue to apply the empty class
watch(localValue, (newVal) => {
  if (selectRef.value) {
    if (newVal === "" || newVal === null || newVal === undefined) {
      selectRef.value.classList.add("empty");
    } else {
      selectRef.value.classList.remove("empty");
    }
  }
});

</script>

<template>
  <div class="relative w-full">
    <select
        ref="selectRef"
        :class="[computedClass, errorMessage && (meta.touched || props.formSubmitted) && (props.showErrorBorder !== false) && '!border-red-600/80']"
        v-bind="_.omit(attrs, 'class')"
        v-model="localValue"
        :name="props.name"
    >
      <slot></slot>
    </select>
    <Transition>
      <div class='text-red-600/80 mt-1 text-[11px]' :class="!relativeError && 'absolute'" v-if="errorMessage && (meta.touched || props.formSubmitted) && !props.hideError">
        {{  capitaliseFirstLetter(errorMessage)  }}
      </div>
    </Transition>
  </div>

</template>
